import {ReactNode, useMemo, useState} from 'react';
import {join} from '../utils';
import {Fade} from '../transition';
import {Layer, Overlay, useKeyboardEffect} from '../core';
import {finalize, from, Observable} from 'rxjs';
import {isFunction} from 'underscore';

import {LoaderSelector} from './loader.selectors';
import styles from './loader.module.less';

type ObservableBuilder<T> = {
  asObservable(): Observable<T>;
}

function isObservableBuilder(target: any): target is ObservableBuilder<any> {
  return 'asObservable' in target && isFunction(target.asObservable);
}

export interface LoaderProps {
	show: boolean;
	className?: string;
	longLoadingText?: string;
	whiteBackground?: boolean;
	children?: ReactNode;
	fullscreen?: boolean;
}

function getLoadingImageURL(white: boolean): string {
	const filename = 'loading' + (white ? '_white' : '') + '.gif';
	return 'https://esgiwebfiles.s3.amazonaws.com/loader/' + filename;
}

export function Loader(props: LoaderProps): JSX.Element {
	const url = useMemo(() => getLoadingImageURL(props.whiteBackground), [props.whiteBackground]);

	useKeyboardEffect('keydown', (e) => {
		if(e.key?.toLowerCase() === 'tab' && props.show) {
			e.preventDefault();
		}
	});

	const className = join(styles.loader, props.fullscreen && styles.fixedLoader, props.whiteBackground && styles.whiteBackground, props.className);

	const loaderContent = <>
		<img width={120} height={120} src={url} alt='Loading...'/>
		<div className={styles.longText}>{props.longLoadingText}</div>
		<div>
			{props.children}
		</div>
	</>;

	let loader = <Overlay className={className} hide={!props.show} data-cy={LoaderSelector}>
		{loaderContent}
	</Overlay>;

	if (props.fullscreen) {
		// Render in separate layer
		loader = <Layer zIndex={10000} preventStackOver inactive={!props.show} aria-busy={props.show}>
			{loader}
		</Layer>;
	}

	return <Fade in={props.show} duration='common'>
		{loader}
	</Fade>;
}

type PromiseOrObservableCreator<T> = () => Observable<T> | Promise<T>;

type LoadingCandidate<T> = PromiseOrObservableCreator<T> | Observable<T> | ObservableBuilder<T>;

type ExecutableCandidate<T> = Observable<T>;

export function useLoading<T>(input: LoadingCandidate<T>): [isLoading: boolean, candidate: ExecutableCandidate<T>] {
	const [isLoading, setIsLoading] = useState(false);
	const outCandidate: ExecutableCandidate<T> = useMemo(() => {
		const target = isFunction(input) ? input() : input;
		setIsLoading(true);
		const asObservable = target instanceof Observable ? target : isObservableBuilder(target) ? target.asObservable() : from(target);

		return asObservable.pipe(finalize(() => setIsLoading(false)));
	}, [input]);

	return [isLoading, outCandidate];
}
